import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/expense/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  // Writes need organization.setting.manage; the page is only useful to them.
  return (
    <ProtectedRoute requiredPermission="organization.setting.manage">
      <ExpenseSettingsPage />
    </ProtectedRoute>
  );
}

import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/reusable-components/header/page-header";
import {
  useFetchExpenseSettings,
  useUpdateExpenseSettings,
} from "@/hooks/api/expense";

function ExpenseSettingsPage() {
  const settingsQuery = useFetchExpenseSettings();
  const updateSettings = useUpdateExpenseSettings();
  const settings = settingsQuery.data;

  const handleToggle = async (approverCannotPay: boolean) => {
    try {
      await updateSettings.mutateAsync({ approver_cannot_pay: approverCannotPay });
      toast.success("Expense settings updated");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Expense Settings"
          description="Configure who may pay an approved expense report."
          isLoading={settingsQuery.isPending || updateSettings.isPending}
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payment</CardTitle>
            <CardDescription>
              The owner, and whoever prepared or submitted a report, can never
              approve or pay it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {settingsQuery.isPending ? (
              <Skeleton className="h-14 w-full rounded" />
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="approver_cannot_pay">
                    Approver cannot pay
                  </Label>
                  <p className="text-xs text-muted-foreground text-justify">
                    When on, the person who approved a report cannot also mark
                    it paid, so every payment involves two people.
                  </p>
                </div>
                <Switch
                  id="approver_cannot_pay"
                  checked={settings?.approver_cannot_pay ?? false}
                  onCheckedChange={(checked) => void handleToggle(checked)}
                  disabled={!settings || updateSettings.isPending}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </SidebarInset>
  );
}
